import React from "react";
import styles from "../styles/alergenos.module.css";
import { FaMagnifyingGlass } from "react-icons/fa6";

import { fmt } from "../domain/alergenosFormat";
import { useAlergenosData } from "../hooks/useAlergenosData";
import { useAlergenosUI } from "../hooks/useAlergenosUI";

export default function Alergenos() {
    const { alergenos, personas, loading, error } = useAlergenosData();

    const {
        q,
        setQ,
        soloConPersonas,
        setSoloConPersonas,
        sortBy,
        setSortBy,
        toggle,
        isOpen,
        text,
        filtered,
        personasPorAlergeno,
        kpis,
    } = useAlergenosUI({ alergenos, personas });

    if (loading) {
        return (
            <div className="page">
                <div className="wrap stack">
                    <div className="titlebar">
                        <h2>Alérgenos</h2>
                    </div>
                    <div className="card soft">Cargando…</div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="page">
                <div className="wrap stack">
                    <div className="titlebar">
                        <h2>Alérgenos</h2>
                    </div>
                    <div className="card warn">
                        <strong>Error:</strong> {error}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={styles.page}>
            <div className={[styles.wrap, styles.stack].join(" ")}>
                <div className={styles.titlebar}>
                    <h2>Alérgenos</h2>
                </div>

                {/* Filtros */}
                <div className={[styles.card, styles["filters-row"]].join(" ")}>
                    <input
                        className={styles.input}
                        placeholder="Buscar por alérgeno o persona…"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                    />
                    <label className={[styles.switch, styles.inline].join(" ")}>
                        <input
                            type="checkbox"
                            checked={soloConPersonas}
                            onChange={(e) => setSoloConPersonas(e.target.checked)}
                        />
                        <span>Solo con personas</span>
                    </label>
                    <select className={styles.input} value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
                        <option value="nombre">Orden: nombre</option>
                        <option value="conteo">Orden: nº de personas</option>
                    </select>
                </div>

                {/* KPIs */}
                <div className={styles.kpis}>
                    <div className={styles.kpi}>
                        <span className={styles["kpi-title"]}>Alérgenos</span>
                        <span className={styles["kpi-value"]}>{kpis.totalAlergenos}</span>
                    </div>
                    <div className={styles.kpi}>
                        <span className={styles["kpi-title"]}>Personas (total)</span>
                        <span className={styles["kpi-value"]}>{kpis.totalPersonas}</span>
                    </div>
                    <div className={styles.kpi}>
                        <span className={styles["kpi-title"]}>Vínculos</span>
                        <span className={styles["kpi-value"]}>{kpis.totalVinculos}</span>
                    </div>
                </div>

                {/* Lista */}
                <section className={styles.section}>
                    <div className={styles.stack}>
                        {filtered.length === 0 ? (
                            <div className={[styles.card, styles.soft, styles.empty].join(" ")}>
                                <h4 style={{ margin: 0 }}>
                                    <FaMagnifyingGlass style={{ fontSize: "30px" }} />
                                </h4>
                                <p className={styles.muted}>Ajusta el término de búsqueda o los filtros.</p>
                            </div>
                        ) : (
                            filtered.map((al) => {
                                const id = al.id;
                                const nombre = al.alergenoDescripcion || al.alergeno || `Alérgeno #${id}`;

                                const personasAl = (personasPorAlergeno[id] || []).filter((p) => {
                                    if (!text) return true;
                                    return [p.nombre, p.apellidos, p.nickname, p.email]
                                        .filter(Boolean)
                                        .some((v) => String(v).toLowerCase().includes(text));
                                });

                                const openAcc = isOpen(id);

                                return (
                                    <article
                                        key={id}
                                        className={[
                                            styles["item-card"],
                                            styles.accordion,
                                            openAcc ? styles.open : "",
                                        ].join(" ").trim()}
                                    >
                                        <button
                                            type="button"
                                            className={styles["accordion-header"]}
                                            onClick={() => toggle(id)}
                                            aria-expanded={openAcc}
                                            aria-controls={`alerg-body-${id}`}
                                        >
                                            <div className={styles["accordion-title"]}>
                                                <strong>{nombre}</strong>
                                                <span className={styles.muted}>
                                                    — {(personasPorAlergeno[id] || []).length} persona(s)
                                                </span>
                                            </div>
                                            <span className={styles.chevron} aria-hidden>
                                                ▾
                                            </span>
                                        </button>

                                        <div id={`alerg-body-${id}`} className={styles["accordion-body"]}>
                                            <div className={styles["item-row"]}>
                                                {personasAl.length === 0 ? (
                                                    <div className={styles["empty-inline"]}>
                                                        No hay personas con este alérgeno.
                                                    </div>
                                                ) : (
                                                    <div className={styles["people-grid"]}>
                                                        {personasAl.map((p) => (
                                                            <div key={p.id} className={styles["person-row"]}>
                                                                <div className={styles.avatar}>
                                                                    <div className={styles.circle}>
                                                                        {(p.nombre || "?").charAt(0)}
                                                                    </div>
                                                                </div>
                                                                <div className={styles.info}>
                                                                    <div className={styles.line1}>
                                                                        <strong>{fmt.personName(p)}</strong>
                                                                    </div>
                                                                    <div className={[styles.line2, styles.muted, styles.small].join(" ")}>
                                                                        {p.email ? p.email : "—"} · {p.telefono ? p.telefono : "—"}
                                                                    </div>
                                                                </div>
                                                                <div className={styles.tags}>
                                                                    {(p.alergenos || [])
                                                                        .filter((a2) => a2.id !== id)
                                                                        .slice(0, 5)
                                                                        .map((a2) => (
                                                                            <span key={a2.id} className={styles.chip}>
                                                                                {a2.alergenoDescripcion || a2.alergeno || `#${a2.id}`}
                                                                            </span>
                                                                        ))}
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </article>
                                );
                            })
                        )}
                    </div>
                </section>
            </div>
        </div>
    );
}
